/**
 * Service pour intégrer Firebase Firestore
 * Sauvegarde les inscriptions dans Firestore
 */

import { collection, addDoc, serverTimestamp, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Ajoute une inscription dans Firestore
 * @param {string} name - Nom complet de l'utilisateur
 * @param {string} email - Email de l'utilisateur
 * @param {string} phone - Téléphone de l'utilisateur (optionnel)
 * @returns {Promise<boolean>} - True si succès, False sinon
 */
// Constantes de validation
const MAX_NAME_LENGTH = 200;
const MAX_EMAIL_LENGTH = 255;
const MAX_PHONE_LENGTH = 20;

// Fonction pour masquer les données sensibles dans les logs
const maskSensitiveData = (data) => {
  if (process.env.NODE_ENV === 'production') {
    return {
      name: data.name ? `${data.name.substring(0, 2)}***` : '***',
      email: data.email ? `${data.email.substring(0, 3)}***` : '***',
      phone: '***'
    };
  }
  return data;
};

export const addToFirebase = async (name, email, phone = '') => {
  // Validation des longueurs
  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const trimmedPhone = phone.trim() || '';

  if (trimmedName.length === 0 || trimmedName.length > MAX_NAME_LENGTH) {
    throw new Error(`Le nom doit contenir entre 1 et ${MAX_NAME_LENGTH} caractères`);
  }

  if (trimmedEmail.length === 0 || trimmedEmail.length > MAX_EMAIL_LENGTH) {
    throw new Error(`L'email doit contenir entre 1 et ${MAX_EMAIL_LENGTH} caractères`);
  }

  if (trimmedPhone.length > MAX_PHONE_LENGTH) {
    throw new Error(`Le téléphone ne peut pas dépasser ${MAX_PHONE_LENGTH} caractères`);
  }

  // Validation format email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedEmail)) {
    throw new Error('Format d\'email invalide');
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('🔥 [Firebase] Début de l\'ajout à Firestore...');
    console.log('🔥 [Firebase] Données:', maskSensitiveData({ name: trimmedName, email: trimmedEmail, phone: trimmedPhone }));
  }

  // Vérifier que Firebase est configuré
  if (!db) {
    console.error('❌ [Firebase] Firestore non initialisé');
    throw new Error('Firebase non configuré. Vérifiez vos variables d\'environnement Firebase.');
  }

  try {
    const startTime = Date.now();

    // Préparer les données à sauvegarder
    const inscriptionData = {
      name: trimmedName,
      email: trimmedEmail.toLowerCase(),
      phone: trimmedPhone,
      createdAt: serverTimestamp(),
      source: 'landing-page'
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('📤 [Firebase] Préparation de la sauvegarde...');
      console.log('📤 [Firebase] Données:', maskSensitiveData(inscriptionData));
    }

    // Ajouter le document dans la collection "inscriptions"
    const docRef = await addDoc(collection(db, 'inscriptions'), inscriptionData);

    const duration = Date.now() - startTime;
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ [Firebase] Inscription sauvegardée avec succès (${duration}ms)`);
      console.log('✅ [Firebase] Document ID:', docRef.id);
      console.log('✅ [Firebase] Collection: inscriptions');
    }
    
    return true;
  } catch (error) {
    // Masquer les logs en production
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ [Firebase] Erreur lors de l\'ajout à Firestore:');
      console.error('❌ [Firebase] Type d\'erreur:', error.constructor.name);
      console.error('❌ [Firebase] Message:', error.message);
      console.error('❌ [Firebase] Code:', error.code);
      
      // Messages d'erreur plus explicites
      if (error.code === 'permission-denied') {
        console.error('❌ [Firebase] Erreur de permissions - Vérifiez vos règles Firestore');
      } else if (error.code === 'unavailable') {
        console.error('❌ [Firebase] Service indisponible - Vérifiez votre connexion');
      } else if (error.code === 'unauthenticated') {
        console.error('❌ [Firebase] Non authentifié - Vérifiez votre configuration Firebase');
      }
    }
    
    // Propager l'erreur
    throw error;
  }
};

// Rate limiting pour checkEmailExists (basique côté client)
const emailCheckCache = new Map();
const EMAIL_CHECK_COOLDOWN = 5000; // 5 secondes entre chaque vérification du même email
const MAX_EMAIL_CHECKS_PER_MINUTE = 10; // Maximum 10 vérifications par minute par IP (approximatif)

/**
 * Vérifie si un email existe déjà dans Firestore
 * Avec rate limiting basique pour limiter l'énumération
 * @param {string} email - Email à vérifier
 * @returns {Promise<boolean>} - True si l'email existe déjà, False sinon
 */
export const checkEmailExists = async (email) => {
  // Vérifier que Firebase est configuré
  if (!db) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ [Firebase] Firestore non initialisé');
    }
    return false;
  }

  if (!email || !email.trim()) {
    return false;
  }

  const emailTrimmed = email.trim().toLowerCase();

  // Validation format email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(emailTrimmed)) {
    return false;
  }

  // Validation longueur
  if (emailTrimmed.length > MAX_EMAIL_LENGTH) {
    return false;
  }

  // Rate limiting basique
  const now = Date.now();
  const cacheKey = emailTrimmed;
  const cached = emailCheckCache.get(cacheKey);

  if (cached && (now - cached.timestamp) < EMAIL_CHECK_COOLDOWN) {
    // Retourner le résultat en cache si la vérification est trop récente
    return cached.result;
  }

  // Compter les vérifications récentes (approximation)
  const recentChecks = Array.from(emailCheckCache.values())
    .filter(item => (now - item.timestamp) < 60000).length;
  
  if (recentChecks >= MAX_EMAIL_CHECKS_PER_MINUTE) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️ [Firebase] Trop de vérifications d\'email, rate limiting activé');
    }
    return false; // Retourner false pour ne pas bloquer, mais limiter les requêtes
  }

  try {
    // Rechercher dans la collection "inscriptions" un document avec cet email
    // limit(1) requis pour respecter les règles Firestore (allow read: if request.query.limit <= 1)
    const q = query(
      collection(db, 'inscriptions'),
      where('email', '==', emailTrimmed),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    // Si on trouve au moins un document, l'email existe déjà
    const result = !querySnapshot.empty;
    
    // Mettre en cache le résultat
    emailCheckCache.set(cacheKey, {
      result,
      timestamp: now
    });

    // Nettoyer le cache toutes les 5 minutes
    if (emailCheckCache.size > 100) {
      const fiveMinutesAgo = now - 300000;
      for (const [key, value] of emailCheckCache.entries()) {
        if (value.timestamp < fiveMinutesAgo) {
          emailCheckCache.delete(key);
        }
      }
    }
    
    return result;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ [Firebase] Erreur lors de la vérification de l\'email:', error);
    }
    // En cas d'erreur, on retourne false pour ne pas bloquer l'utilisateur
    return false;
  }
};
